import { readonly } from '@vue/composition-api';
import {
  StorageSerializers,
  useEventListener,
  useSessionStorage
} from '@vueuse/core';

import { openURL } from '@/modules/base/composables/utils.composable';

export interface ProzisStoreDeviceData {
  channel: string;
  deviceToken: string;
  language: string;
  store: string;
}

/**
 * Internal reactive `prozisStoreDeviceData` on SessionStorage.
 */
const prozisStoreDeviceDataInternal =
  useSessionStorage<ProzisStoreDeviceData | null>(
    'prozisStoreDeviceData',
    null,
    {
      serializer: StorageSerializers.object
    }
  );

/**
 * Reactive `prozisStorePushActive` on SessionStorage.
 */
export const prozisStorePushActive = useSessionStorage<boolean | null>(
  'prozisStorePushActive',
  null,
  { serializer: StorageSerializers.boolean }
);

/**
 * Reactive `prozisStorePushActiveLastValidationDate` on SessionStorage.
 */
export const prozisStorePushActiveLastValidationDate = useSessionStorage<
number | null
>('prozisStorePushActiveLastValidationDate', null, {
  serializer: StorageSerializers.number
});

/**
 * Reactive `prozisStoreDeviceData` on LocalStorage.
 */
export const prozisStoreDeviceData = readonly(prozisStoreDeviceDataInternal);

/**
 * Function to clear `prozisStoreDeviceData` from LocalStorage.
 */
export const clearProzisStoreDeviceData = () => {
  prozisStoreDeviceDataInternal.value = null;
};

/**
 * Attribute that forces anchors (on itself or inside it) to be opened on the
 * external browser, even if they are `www.prozis.com` urls.
 */
export const OPEN_EXTERNAL_ATTRIBUTE = 'data-open-external';

let installed = false;

/**
 * Defines prozis store plugin.
 */
export const installProzisStorePlugin = () => {
  if (installed) {
    return;
  }

  installed = true;

  /**
   * Adds an event listener for click events to handle anchor (`<a>`) tag clicks.
   * Prevents default behavior and uses a custom navigation for Prozis Store.
   */
  useEventListener(
    'click',
    (event) => {
      const { target: eventTarget } = event || {};
      const { href, nodeName, rel, target } = (eventTarget ||
        {}) as HTMLAnchorElement;

      if (nodeName === 'A') {
        event.preventDefault();

        openURL({
          features: rel || 'noopener noreferrer',
          forceExternal: !!(eventTarget as HTMLElement).closest(
            `[${OPEN_EXTERNAL_ATTRIBUTE}]`
          ),
          target: target === '_blank' ? '_blank' : '_self',
          url: href
        });
      }
    },
    { capture: true }
  );

  /**
   * Defines partnership api to be used by Prozis Store.
   */
  window.partnership = {
    ...window.partnership,
    setDeviceData: (data: string) => {
      try {
        console.info('Received Prozis Store device data', data);

        prozisStoreDeviceDataInternal.value = JSON.parse(data);
      } catch (error) {
        console.error(
          'Error writing prozis store device data to localStorage',
          error
        );
      }
    }
  };
};
